import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useDocumentEditor } from '@/stores/document/editor';

import { useAppSnackbar } from '@/stores/app/snackbar';
import { useSideBySideView } from '@/stores/document/sideBySideView';

import {
  addScript, waitForCordSdk, updateCordMessage, uploadFileToCord, getPostedCordMessage,
} from '@/helpers/thirdParty/cord';
import { vueCordToken } from '@/api/vue';
import { toBlob } from 'html-to-image';
import { hideElementsForDownload } from '@/helpers/fileDownload';

export const useCommenting = defineStore('commenting', () => {
  const editorStore = useDocumentEditor();
  const sideBySideViewStore = useSideBySideView();

  const isCordLoading = ref(false);
  const cordLoadFailed = ref(false);

  const setCordLoading = (loading) => {
    isCordLoading.value = loading;
  };

  const loadCord = async ({ jobId } = {}) => {
    try {
      setCordLoading(true);
      await addScript();
      const { token } = await vueCordToken({ jobId });
      const cordSdk = await waitForCordSdk();
      cordSdk.init({ client_auth_token: token });
      cordLoadFailed.value = false;
    } catch (e) {
      if (!cordLoadFailed.value) {
        cordLoadFailed.value = true;
        // show the error only once
        useAppSnackbar().addErrorSnackbar({
          text: `Failed to load comments: ${e.message}`,
        });
      }
      throw e;
    } finally {
      setCordLoading(false);
    }
  };

  const moveAllListItemsOnTheTopLevel = (nodes, depth = 0, parent = null, topNodes = null) => {
    if (depth > 50) {
      return [];
    }

    return nodes.reduce((res, node) => {
      if (node.type === 'listItem') {
        // eslint-disable-next-line no-param-reassign
        node.parentType = parent.type;
        topNodes.push(node);
        moveAllListItemsOnTheTopLevel(node.content, depth + 1, node, topNodes);
      } else {
        // eslint-disable-next-line no-param-reassign
        node.content = moveAllListItemsOnTheTopLevel(
          node.content,
          depth + 1,
          node,
          depth === 0 ? res : topNodes,
        );
        res.push(node);
      }
      return res;
    }, []);
  };

  const tiptapJsonToCordContent = (nodes, depth = 0) => {
    if (depth > 50) {
      return [];
    }

    let bulletNumber = 0;
    return nodes.reduce((res, node) => {
      if (!(node.type === 'listItem' && node.parentType === 'orderedList')) {
        bulletNumber = 0;
      }

      const children = tiptapJsonToCordContent(node.content, depth + 1);

      if (node.type === 'text') {
        const { text = '', marks = [] } = node;
        const link = marks.find((mark) => mark.type === 'link');

        const textResult = {
          text,
          bold: marks.some((mark) => mark.type === 'bold'),
          italic: marks.some((mark) => mark.type === 'italic'),
        };

        if (link) {
          return [...res, {
            type: 'link',
            url: String(link.attrs?.href || ''),
            children: [textResult],
          }];
        }

        return [...res, textResult];
      }

      if (node.type === 'bulletList' || node.type === 'orderedList') {
        return res;
      }

      // all other node types should have children
      if (children.length === 0) {
        return res;
      }

      if (node.type === 'paragraph') {
        return [...res, { type: 'p', children }];
      }

      if (node.type === 'listItem') {
        if (node.parentType === 'orderedList') {
          bulletNumber += 1;
          return [...res, { type: 'number_bullet', bulletNumber, children }];
        }

        return [...res, { type: 'bullet', children }];
      }

      if (node.type === 'heading') {
        const { level } = node.attrs;

        return [...res, { type: 'p', class: `h${level}`, children }];
      }

      const error = new Error(`Unhandled tiptap node type: ${node.type}`);
      error.node = node;
      throw error;
    }, []);
  };

  const editorSelectionInCordFormat = computed(() => {
    const nodes = JSON.parse(JSON.stringify(editorStore.selectedJson));
    return tiptapJsonToCordContent(moveAllListItemsOnTheTopLevel(nodes));
  });

  const showInlineCommentModal = ref(false);
  const inlineCommentSelection = ref(null);
  const inlineEditorContentInCordFormat = ref(null);

  const setShowInlineCommentModal = (show) => {
    inlineCommentSelection.value = editorStore.selectedHtml;
    inlineEditorContentInCordFormat.value = JSON.parse(JSON.stringify(
      editorSelectionInCordFormat.value,
    ));
    showInlineCommentModal.value = show;
  };

  const commentSelection = async () => {
    setShowInlineCommentModal(true);
  };

  const addClassToTopLevelCordNodes = (nodes, className) => nodes.map((node) => ({
    ...node,
    class: className,
  }));

  /** Convert plain text to Cord content blocks with class 'original-quote' (one paragraph per line). */
  const plainTextToCordContent = (plainText) => {
    const trimmed = (plainText || '').trim();

    if (!trimmed) return [];

    const lines = trimmed.split(/\n/);

    return lines.map((line) => ({
      type: 'p',
      class: 'original-quote',
      children: [{ text: line, bold: false, italic: false }],
    }));
  };

  const addQuotedPlainTextToInlineMessage = async ({ threadId, messageId, plainText }) => {
    try {
      setCordLoading(true);

      const message = await getPostedCordMessage(messageId);
      const quotedContent = plainTextToCordContent(plainText);

      await updateCordMessage({
        threadId,
        messageId: message.id,
        content: [...quotedContent, ...message.content],
      });
    } finally {
      setCordLoading(false);
    }
  };

  const addEditorSelectionContentToInlineMessage = async ({ threadId, messageId }) => {
    try {
      setCordLoading(true);

      const message = await getPostedCordMessage(messageId);

      // TODO: ask Cord maintainers about removing "Edited" flag of updated message
      await updateCordMessage({
        threadId,
        messageId: message.id,
        content: [
          ...addClassToTopLevelCordNodes(inlineEditorContentInCordFormat.value, 'original-quote'),
          ...message.content,
        ],
      });
    } finally {
      setCordLoading(false);
    }
  };

  const addScreenshotToWidgetMessage = async ({ threadId, messageId, widgetEl }) => {
    try {
      setCordLoading(true);

      const { showHiddenDownloadElements } = hideElementsForDownload(widgetEl);

      const [message, blob] = await Promise.all([
        getPostedCordMessage(messageId),
        toBlob(widgetEl),
      ]);

      showHiddenDownloadElements();

      const id = await uploadFileToCord({ blob, name: `${Date.now()}.png` });

      await updateCordMessage({
        threadId, messageId: message.id, addAttachments: [id],
      });
    } finally {
      setCordLoading(false);
    }
  };

  const show = ref(false);

  const setShowComments = (value) => {
    show.value = value;
  };

  const showCommentsToggle = computed(() => {
    if (sideBySideViewStore.sideBySideView) {
      return false;
    }

    return true;
  });

  const showComments = computed(() => showCommentsToggle.value && show.value);

  return {
    isCordLoading,
    showCommentsToggle,
    showComments,
    editorSelectionInCordFormat,

    loadCord,
    setCordLoading,
    setShowComments,
    commentSelection,

    showInlineCommentModal,
    inlineCommentSelection,
    setShowInlineCommentModal,
    addEditorSelectionContentToInlineMessage,
    addQuotedPlainTextToInlineMessage,
    addScreenshotToWidgetMessage,
  };
});
